import { z } from 'zod';

/**
 * What a Process creates on every firing until Recipes arrive (phase 5). Stored in `Process.trigger`
 * as `{ kind: 'task_template', ... }`; title and description may use `{{date}}` and `{{week}}`.
 */
export const TASK_TEMPLATE_KIND = 'task_template' as const;

export const taskTemplateSchema = z.object({
  kind: z.literal(TASK_TEMPLATE_KIND),
  title: z.string().trim().min(1).max(300),
  description: z.string().max(20_000).nullable().optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).optional(),
  assigneeId: z.string().min(1).nullable().optional(),
  projectId: z.string().min(1).nullable().optional(),
});

export type TaskTemplate = z.infer<typeof taskTemplateSchema>;

/** The template stored in `Process.trigger`, or null when the trigger is missing or of another kind. */
export function parseTaskTemplate(trigger: unknown): TaskTemplate | null {
  const r = taskTemplateSchema.safeParse(trigger);
  return r.success ? r.data : null;
}

export type SlotVariables = {
  /** "05.09.2026" — the slot's calendar date in the process timezone */
  date: string;
  /** "2026-W36" — ISO week of that date */
  week: string;
};

/** Calendar date of an instant in a timezone (Intl only, no library). */
export function localDateParts(at: Date, tz: string): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/** ISO-8601 week number and week-based year for a calendar date. */
export function isoWeek(year: number, month: number, day: number): { year: number; week: number } {
  const d = new Date(Date.UTC(year, month - 1, day));
  const weekday = d.getUTCDay() || 7; // Monday = 1 … Sunday = 7
  d.setUTCDate(d.getUTCDate() + 4 - weekday); // Thursday of this ISO week decides the year
  const isoYear = d.getUTCFullYear();
  const jan1 = Date.UTC(isoYear, 0, 1);
  const week = Math.ceil(((d.getTime() - jan1) / 86_400_000 + 1) / 7);
  return { year: isoYear, week };
}

const two = (n: number) => String(n).padStart(2, '0');

export function slotVariables(slot: Date, tz: string): SlotVariables {
  const { year, month, day } = localDateParts(slot, tz);
  const w = isoWeek(year, month, day);
  return { date: `${two(day)}.${two(month)}.${year}`, week: `${w.year}-W${two(w.week)}` };
}

/** Replaces `{{name}}` placeholders; unknown placeholders stay as written so a typo is visible, not silent. */
export function renderTemplate(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (whole, name: string) => (name in vars ? vars[name]! : whole));
}
